// ============================================================================
// JARVISTRAVEL - TOAST STORE
// The module-level state behind <Toaster />, kept out of the component file.
// ============================================================================
//
// WHY THIS IS A SEPARATE FILE (JAR-1328 review).
//
// It all lived in Toast.tsx, and adding the `__resetToasts` test seam there
// broke the build: `react-refresh/only-export-components` flags a non-component
// export from a file that exports a component, web-app lints with
// `--max-warnings 0`, and the Quality Gate is a required check. The PR failed
// on its own seam.
//
// `export const toast = {...}` had survived that rule because `allowConstantExport`
// permits it; `export function __resetToasts()` is a function declaration and
// does not qualify. Reaching for an eslint-disable would have left the real
// problem in place — a component file owning mutable module state that anything
// in the app can write to, which is what made the leak this ticket fixes
// possible. The rule's own message says the fix: "Use a new file to share
// constants or functions between components."
//
// So Toast.tsx is now only the renderer, and this file is only the state.

export type ToastType = 'success' | 'error' | 'info';

/** A toast's one button, beside its message: Undo, Try again. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  action?: ToastAction;
}

// Cap concurrent toasts so a burst (e.g. a retry storm) can't stack endlessly.
const MAX_TOASTS = 4;

let items: ToastItem[] = [];
const listeners = new Set<() => void>();
let nextId = 1;
// The auto-dismiss timers still to fire, so a reset can stop them.
const timers = new Set<ReturnType<typeof setTimeout>>();

const emit = () => listeners.forEach((l) => l());

/** useSyncExternalStore's subscribe half. */
export const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** useSyncExternalStore's snapshot half. Returns the same reference until a
 *  mutation replaces `items`, which is what stops an infinite re-render. */
export const getSnapshot = () => items;

export function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(type: ToastType, message: string, durationMs: number, action?: ToastAction): number {
  // The same words already on screen aren't stacked again: a screen that reports
  // a trip's refusal in core's words, over App's toast of it (JAR-1860), or one
  // save failing in several parts at once. Only within a kind: an error saying
  // what an info said is still an error, and is announced as one.
  // A toast with a button replaces the one it repeats instead: its button acts
  // on the newer change.
  const shown = items.find((t) => t.type === type && t.message === message);
  if (shown && !action) return shown.id;
  const id = nextId++;
  // A toast carrying an action is someone's only way back - an Undo, a failed
  // re-plan's "Re-plan now" - and is exempt from the burst cap: evicting it
  // strands the user with neither the banner nor the retry (JAR-2728 review).
  // Only plain timed announcements overflow.
  const kept = items.filter((t) => t !== shown);
  const trimmed = action ? kept.slice(-(MAX_TOASTS - 1)) : kept.slice(-(MAX_TOASTS - 1));
  items = [...trimmed, { id, type, message, action }];
  emit();
  // One with a button stays until it's used or dismissed: an Undo on a timer is
  // a way back that can go before it's read.
  if (durationMs > 0 && !action) {
    const timer = setTimeout(() => {
      timers.delete(timer);
      dismiss(id);
    }, durationMs);
    timers.add(timer);
  }
  return id;
}

/** Fire a transient notification from anywhere (React or not). Errors linger a
 *  little longer and are announced assertively; success/info auto-dismiss. One
 *  with an action (Undo, Try again) stays until it's used or dismissed. */
export const toast = {
  success: (message: string, action?: ToastAction) => push('success', message, 4000, action),
  error: (message: string, action?: ToastAction) => push('error', message, 6000, action),
  info: (message: string, action?: ToastAction) => push('info', message, 4000, action),
  dismiss,
};

/** Test seam: empty the stack between cases.
 *
 *  Unlike the other module-level stores, this one is RENDERED. A toast fired in
 *  one test is still in `items` for the next, so any later test that mounts
 *  <Toaster /> sees it and a getByText elsewhere can match it. Auto-dismiss does
 *  not save you: it is a setTimeout, and a fast test finishes first (JAR-1328).
 *
 *  nextId is reset too, so ids do not depend on how many tests ran before.
 *  So are the dismiss timers: ids restart at 1, and a timer an earlier case
 *  left running would take a later case's toast with its id. */
export function __resetToasts(): void {
  timers.forEach(clearTimeout);
  timers.clear();
  items = [];
  nextId = 1;
  emit();
}
